import {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import CardsContainer from "@/components/CardsContainer";
import CardsList from "@/components/CardsList";
import CardsTable from "@/components/CardsTable";
import SwitchView from "@/components/SwitchView";
import StatsContainer from "@/components/StatsContainer";
import VLFModal from "@/components/Modal";
import VLFSelectNew from "@/components/Input/SelectNew";

import {PlayerAPI} from "@/api/player";

import AddIcon from "@mui/icons-material/Add";

import {
	dateFormat,
	tqrOptions,
	ERROR_OPERATION,
	SUCCESSFUL_OPERATION,
	REQUIRED_FIELD,
} from "@/utils/constants";
import {checkRequiredFields} from "@/utils/functions";
import {useCategories} from "@/utils/hooks";

import "./index.scss";

const fieldDateFormat = "YYYY-MM-DD";

const requiredFields = ["tqrValue"];

function TQRPage(props) {
	const {handleNotification} = props;
	const navigate = useNavigate();

	const playerId = useSelector((state) => state?.user?.playerId);
	const userRole = useSelector((state) => state?.user?.roleName);
	const isPlayer = userRole === "Player";

	const defaultFiltersData = {
		fromDate: moment()
			.startOf(isPlayer ? "month" : "day")
			.format(fieldDateFormat),
		toDate: moment()
			.endOf(isPlayer ? "month" : "day")
			.format(fieldDateFormat),
	};

	const [loading, setLoading] = useState(false);
	const [currentSwitchView, setCurrentSwitchView] = useState("list");

	const {categories} = useCategories({
		setLoading: false,
		handleNotification: undefined,
		onlyPro: true,
	});

	const [tqr, setTqr] = useState([]);
	const [filtersData, setFiltersData] = useState(
		JSON.parse(
			sessionStorage.getItem("tqr_filters" + isPlayer ? "_player" : ""),
		) || defaultFiltersData,
	);

	useEffect(() => {
		if (filtersData) {
			sessionStorage.setItem(
				"tqr_filters" + isPlayer ? "_player" : "",
				JSON.stringify(filtersData),
			);
		}
	}, [filtersData]);

	const filters = [
		{
			key: "fromDate",
			type: "date",
			label: "Da",
			customClassName: "col-6 col-sm-4",
			clearable: false,
			value: moment(filtersData?.fromDate).format(fieldDateFormat),
			setValue: (e) => {
				if (e) {
					const filters = {
						...filtersData,
						fromDate: moment(e).format(fieldDateFormat),
					};
					setFiltersData(filters);
				}
			},
		},
		{
			key: "toDate",
			type: "date",
			label: "A",
			customClassName: "col-6 col-sm-4",
			clearable: false,
			value: moment(filtersData?.toDate).format(fieldDateFormat),
			setValue: (e) => {
				if (e) {
					const filters = {
						...filtersData,
						toDate: moment(e).format(fieldDateFormat),
					};
					setFiltersData(filters);
				}
			},
		},
		// {
		// 	key: "categoryId",
		// 	type: "select",
		// 	options: categories.map((c) => {
		// 		return {
		// 			value: c.id,
		// 			label: c.name,
		// 		};
		// 	}),
		// 	sorted: false,
		// 	label: "Categoria",
		// 	customClassName: "col-12 col-sm-4",
		// 	clearable: true,
		// 	value: filtersData?.categoryId,
		// 	setValue: (value) => {
		// 		setFiltersData({...filtersData, categoryId: value});
		// 	},
		// },
	];

	const [openHandleConfirm, setOpenHandleConfirm] = useState(false);
	const [openHandleEdit, setOpenHandleEdit] = useState(false);

	const [value, setValue] = useState();
	const [currentTQR, setCurrentTQR] = useState({});
	const [errors, setErrors] = useState([]);

	const getTQR = async () => {
		setLoading(true);
		try {
			const response = isPlayer
				? await PlayerAPI.playerListTQR(playerId, filtersData)
				: await PlayerAPI.listTQR(filtersData);
			setTqr(response);
		} catch (error) {
			console.log(error);
		} finally {
			setLoading(false);
		}
	};

	const deleteTQR = async () => {
		setLoading(true);
		try {
			await PlayerAPI.deleteTQR(playerId, currentTQR?.id);
			getTQR();
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		} finally {
			setOpenHandleConfirm(false);
		}
	};

	const updateTQR = async () => {
		setLoading(true);
		try {
			await PlayerAPI.updateTQR(
				isPlayer ? playerId : currentTQR?.playerId,
				currentTQR?.id,
				{
					...currentTQR,
					tqrValue: value?.value,
				},
			);
			getTQR();
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		} finally {
			setOpenHandleEdit(false);
		}
	};

	useEffect(() => {
		if (currentSwitchView === "list") {
			getTQR();
		}
	}, [filtersData, currentSwitchView]);

	const columns = [
		{
			label: "Data",
			key: "date",
			customRender: (item) => {
				return moment(item.tqrDate).format(dateFormat);
			},
			small: true,
		},
	];

	if (!isPlayer) {
		columns.push({
			label: "Giocatore",
			key: "playerId",
			customRender: (item) =>
				item.player
					? item.player.firstName + " " + item.player.lastName
					: "---",
			align: "start",
		});
	}

	columns.push({
		label: "TQR",
		key: "tqr",
		customRender: (item) => {
			return !item.emptyRow
				? tqrOptions.filter((o) => o.value === item.tqrValue)?.[0]?.label
				: "Nessun dato inserito";
		},
		align: "start",
	});

	const handleEdit = (item) => {
		setValue({
			value: item.tqrValue,
			label: tqrOptions.filter((o) => o.value === item.tqrValue)?.[0]?.label,
		});
		setCurrentTQR(item);
		setOpenHandleEdit(true);
	};

	const mainActions = [
		{
			icon: <AddIcon />,
			onClick: () => navigate("/measurations/tqr/new"),
		},
	];

	const moveActions = [
		{
			variant: "outlined",
			label: isPlayer ? "<< mese" : "<< giorno",
			onClick: () => {
				setFiltersData({
					...filtersData,
					fromDate: moment(filtersData?.fromDate)
						.subtract(1, isPlayer ? "months" : "days")
						.format(fieldDateFormat),
					toDate: moment(filtersData?.toDate)
						.subtract(1, isPlayer ? "months" : "days")
						.format(fieldDateFormat),
				});
			},
		},
		{
			variant: "outlined",
			label: isPlayer ? "mese >>" : "giorno >>",
			onClick: () => {
				setFiltersData({
					...filtersData,
					fromDate: moment(filtersData?.fromDate)
						.add(1, isPlayer ? "months" : "days")
						.format(fieldDateFormat),
					toDate: moment(filtersData?.toDate)
						.add(1, isPlayer ? "months" : "days")
						.format(fieldDateFormat),
				});
			},
		},
	];

	const tqrData = [...tqr];

	// const groupedTQR = {};
	// categories
	// 	.filter((c) => c.name !== "Prima Squadra")
	// 	.forEach((c) => {
	// 		const currentCategory = c.name;
	// 		const tqrItems = tqrData?.filter((t) => {
	// 			return t.playerCategoryName === currentCategory;
	// 		});
	// 		groupedTQR[currentCategory] = tqrItems;
	// 	});

	const listConfiguration = {
		data: tqrData,
		handleEdit,
		handleDelete: (item) => {
			setCurrentTQR(item);
			setOpenHandleConfirm(true);
		},
	};

	return (
		<Page>
			<Loading visible={loading} />
			{!isPlayer && (
				<SwitchView
					currentSwitchView={currentSwitchView}
					setCurrentSwitchView={setCurrentSwitchView}
					pageOptions={[
						{value: "list", label: "Dati temporali"},
						{value: "stats", label: "Overview"},
					]}
				/>
			)}
			<Breadcrumbs
				actions={
					<MainActions
						actions={
							isPlayer
								? [...moveActions, ...mainActions]
								: currentSwitchView === "list"
									? [...moveActions, ...mainActions]
									: []
						}
					/>
				}
			/>
			{currentSwitchView === "list" ? (
				<CardsContainer
					tableComponent={
						<CardsTable
							filtersData={filters}
							configurationColumns={columns}
							data={tqrData}
							rowActions
							handleDelete={(item) => {
								setCurrentTQR(item);
								setOpenHandleConfirm(true);
							}}
							handleEdit={handleEdit}
							defaultSortedField="tqrDate"
							defaultSortedOrder="descend"
						/>
					}
					listComponent={
						<CardsList
							type="tqr"
							breadcrumbs
							filtersData={filters}
							listConfiguration={listConfiguration}
							defaultSortedField="tqrDate"
							defaultSortedOrder="descend"
						/>
					}
				/>
			) : (
				<StatsContainer type="tqr" categories={categories} />
			)}
			{openHandleConfirm && (
				<VLFModal
					open={openHandleConfirm}
					title="Eliminazione"
					content={
						<div className="mt-2">
							Sei sicuro di voler eliminare questo elemento?
						</div>
					}
					confirm={deleteTQR}
					confirmLabel="Conferma"
					close={() => setOpenHandleConfirm(false)}
				/>
			)}
			{openHandleEdit && (
				<VLFModal
					open={openHandleEdit}
					title="Modifica valore"
					content={
						<div className="row">
							<div className={"col-12"}>
								<VLFSelectNew
									options={tqrOptions}
									value={value || ""}
									onChange={(value) => {
										setErrors([]);
										setValue(value);
									}}
									required
									label="TQR"
									sorted={false}
									helperText={
										errors.indexOf("tqrValue") !== -1 ? REQUIRED_FIELD : ""
									}
									error={errors.indexOf("tqrValue") !== -1}
								/>
							</div>
						</div>
					}
					confirm={() => {
						const errors = checkRequiredFields(requiredFields, {
							tqrValue: value,
						});
						setErrors(errors);

						if (!errors.length) {
							updateTQR();
						}
					}}
					confirmLabel="Conferma"
					close={() => setOpenHandleEdit(false)}
				/>
			)}
		</Page>
	);
}

export default TQRPage;
